import _ from 'lodash';
import {DATA_SETS} from '../Constants';
import {PREFIX_RH, PREFIX_T} from './behaviour_models/shared/behaviour_models_utils';

export const LAG_TEMP_RH_OPTIONS = {
  OFF: 0,
  TWO_HOURS: 2
};

const MS_PER_HOUR = 60 * 60 * 1000;

const LAG_SLICE_BY_DATA_SET = {
  [DATA_SETS.MCARTHUR_MK5]: s => s.point_data_mcarthur_mk5,
  [DATA_SETS.LEAFLET_80]: s => s.point_data_leaflet_80,
  [DATA_SETS.VESTA]: s => s.point_data_vesta,
  [DATA_SETS.VESTA_MK2_DRY]: s => s.point_data_vesta_mk2_dry,
  [DATA_SETS.VESTA_MK2_WET]: s => s.point_data_vesta_mk2_wet,
  [DATA_SETS.MALLEE_HEATH]: s => s.point_data_mallee_heath
};

function applyRowModifications(wd, mods) {
  return mods ? { ...wd, ...mods } : wd;
}

export function buildWeatherIndex(weather_data, modifications) {
  const index = new Map();
  _.values(weather_data).forEach((wd) => {
    const mods = modifications && modifications[wd.name];
    const row = applyRowModifications(wd, mods);
    if (typeof row.sortIndex === 'number') {
      index.set(row.sortIndex, row);
    }
  });
  return index;
}

export function lagHoursFromVariables(variables) {
  if (variables && variables.lag_temp_rh === LAG_TEMP_RH_OPTIONS.TWO_HOURS) {
    return 2;
  }
  return 0;
}

export function lagHoursForDataSet(state, data_set) {
  const pick = LAG_SLICE_BY_DATA_SET[data_set];
  if (!pick) {
    return 0;
  }
  return lagHoursFromVariables(pick(state));
}

function resolveLagTempRh(wd, weatherIndex, lagHours) {
  if (!wd || typeof wd.sortIndex !== 'number') {
    return undefined;
  }

  if (!lagHours) {
    const temp = wd[PREFIX_T];
    const rh = wd[PREFIX_RH];
    if (typeof temp === 'undefined' || typeof rh === 'undefined') {
      return undefined;
    }
    return { temp, rh };
  }

  const lagRow = weatherIndex.get(wd.sortIndex - lagHours * MS_PER_HOUR);
  if (!lagRow) {
    return undefined;
  }

  const temp = lagRow[PREFIX_T];
  const rh = lagRow[PREFIX_RH];
  if (typeof temp === 'undefined' || typeof rh === 'undefined') {
    return undefined;
  }
  return { temp, rh };
}

/**
 * Returns a copy of weather table rows with HP_T_SFC / HP_RH_SFC replaced by values
 * from lagHours earlier (manual-weather overrides on the source hour are included).
 */
export function applyLagTempRhShift(weather_data, modifications, lagHours) {
  if (!lagHours || !weather_data) {
    return weather_data;
  }

  const index = buildWeatherIndex(weather_data, modifications);
  const shifted = {};
  _.values(weather_data).forEach((wd) => {
    const tr = resolveLagTempRh(wd, index, lagHours);
    shifted[wd.name] = {
      ...wd,
      [PREFIX_T]: tr ? tr.temp : undefined,
      [PREFIX_RH]: tr ? tr.rh : undefined
    };
  });
  return shifted;
}

export function lagTempRhLabel(value) {
  return value === LAG_TEMP_RH_OPTIONS.TWO_HOURS ? '2 Hrs' : 'Off';
}

function findRowBySortIndex(weather_data, sortIndex) {
  return _.find(_.values(weather_data), wd => wd.sortIndex === sortIndex) || null;
}

/** Raw weather row at sortIndex − lagHours for a display row (null when lag off or missing). */
export function lagSourceRow(weather_data, displayRow, lagHours) {
  if (!lagHours || !displayRow || typeof displayRow.sortIndex !== 'number') {
    return null;
  }
  return findRowBySortIndex(weather_data, displayRow.sortIndex - lagHours * MS_PER_HOUR);
}

/** True when lag is on but this display row has no source hour (top-of-table gap cells). */
export function isLagTempRhGap(weather_data, displayRow, lagHours) {
  return Boolean(lagHours && displayRow && !lagSourceRow(weather_data, displayRow, lagHours));
}

/**
 * When lag is on, T/RH manual edits are stored on the source hour but shown on the display row
 * (source + lagHours). Other fields keep modifications on their original row.
 */
export function displayModificationsForLag(weather_data, modifications, lagHours) {
  if (!modifications) {
    return {};
  }
  if (!lagHours) {
    return modifications;
  }

  const display = {};
  _.forOwn(modifications, (rowMods, rowName) => {
    const kept = {};
    _.forOwn(rowMods, (val, key) => {
      if (key !== PREFIX_T && key !== PREFIX_RH) {
        kept[key] = val;
      }
    });
    if (Object.keys(kept).length > 0) {
      display[rowName] = kept;
    }
  });

  _.values(weather_data).forEach((wd) => {
    const source = lagSourceRow(weather_data, wd, lagHours);
    if (!source) {
      return;
    }
    const sourceMods = modifications[source.name];
    if (!sourceMods) {
      return;
    }
    const tr = {};
    if (typeof sourceMods[PREFIX_T] !== 'undefined') {
      tr[PREFIX_T] = sourceMods[PREFIX_T];
    }
    if (typeof sourceMods[PREFIX_RH] !== 'undefined') {
      tr[PREFIX_RH] = sourceMods[PREFIX_RH];
    }
    if (Object.keys(tr).length > 0) {
      display[wd.name] = { ...(display[wd.name] || {}), ...tr };
    }
  });

  return display;
}



// WEBPACK FOOTER //
// ./src/utils/lag_weather_utils.js